import React, { useState } from 'react';
import InfoTip from '../../shared/InfoTip';
import { fmtNumber, fmtPercent } from '../../shared/format';
import { SkeletonText } from '../../shared/Skeleton';
import { ErrorState } from '../../shared/StateMessage';
import { parseAdAssets, prettyAdType, titleCaseLabel } from '../shared';

// How many headlines to show before collapsing behind a "+N more" toggle. RSAs
// can carry 15 headlines — showing them all makes one ad dwarf the others.
const VISIBLE_CHIPS = 6;

const AdCard = ({ ad, rank, palette }) => {
  const [showAll, setShowAll] = useState(false);
  const assets = parseAdAssets(ad.ad_title);
  const hasAssets = assets.length > 0;
  const visibleAssets = showAll ? assets : assets.slice(0, VISIBLE_CHIPS);
  const hiddenCount = assets.length - visibleAssets.length;

  // Primary label: the ad group it lives in (human context), not the raw pipe
  // dump. Ad-group / campaign names are internal labels, so title-casing them is
  // safe (headlines below stay verbatim).
  const primaryLabel = titleCaseLabel(ad.adgroup_name || ad.campaign_name) || prettyAdType(ad.ad_type);

  return (
    <div className="px-4 py-4">
      {/* Header: context + rank */}
      <div className="flex items-start justify-between gap-3 mb-2.5">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 leading-snug truncate">
            {primaryLabel}
          </p>
          {ad.campaign_name && ad.adgroup_name && (
            <p className="text-[12px] text-gray-400 dark:text-gray-500 truncate">
              {titleCaseLabel(ad.campaign_name)}
            </p>
          )}
          {/* Ad-level identity — ad groups often hold several ad variants, so the
              same group name can legitimately appear in both Top and Underperforming
              lists. The type + ID make clear these are different ads. */}
          <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 mt-1">
            {ad.ad_type && (
              <span className="shrink-0 inline-flex items-center px-1.5 py-px rounded text-[10px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                {prettyAdType(ad.ad_type)}
              </span>
            )}
            {ad.ad_id && (
              <span className="text-[10px] text-gray-400 dark:text-gray-600">Ad {ad.ad_id}</span>
            )}
          </div>
        </div>
        <span className={`shrink-0 inline-flex items-center justify-center min-w-[24px] h-5 px-1.5 rounded text-[12px] font-bold ${palette.badge}`}>
          #{rank}
        </span>
      </div>

      {/* Metric strip — CTR carries the color signal; size stays inline with the
          other metrics so it isn't double-emphasised. */}
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-3 text-xs text-gray-500 dark:text-gray-400">
        <span>
          <span className={`font-semibold ${palette.ctr}`}>{fmtPercent(ad.ctr)}</span> CTR
        </span>
        <span>{ad.clicks.toLocaleString()} clicks</span>
        <span>{fmtNumber(ad.conversions, true)} conv.</span>
        {ad.impressions != null && <span>{ad.impressions.toLocaleString()} impr.</span>}
      </div>

      {/* Headlines: the literal ad assets, verbatim (no re-casing). Rendered as
          plain text separated by a spaced pipe — headlines contain commas
          ("Sedona Cabins, No RV Needed"), so a faint middot blurs into them; a
          wider, clearer "|" divider reads as a real break without boxing each. */}
      {hasAssets ? (
        <p className="text-[13px] leading-relaxed text-gray-600 dark:text-gray-300">
          {visibleAssets.map((asset, index) => (
            <React.Fragment key={`${asset}-${index}`}>
              {index > 0 && <span className="mx-2 text-gray-300 dark:text-gray-600" aria-hidden="true">|</span>}
              {asset}
            </React.Fragment>
          ))}
          {hiddenCount > 0 && (
            <>
              <span className="mx-2 text-gray-300 dark:text-gray-600" aria-hidden="true">|</span>
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="font-medium text-[#32B1FF] hover:underline"
              >
                +{hiddenCount} more
              </button>
            </>
          )}
          {showAll && assets.length > VISIBLE_CHIPS && (
            <>
              <span className="mx-2 text-gray-300 dark:text-gray-600" aria-hidden="true">|</span>
              <button
                type="button"
                onClick={() => setShowAll(false)}
                className="font-medium text-gray-400 dark:text-gray-500 hover:underline"
              >
                Show less
              </button>
            </>
          )}
        </p>
      ) : (
        <span className="text-[12px] text-gray-400 dark:text-gray-500 italic">
          No headline text available for this ad.
        </span>
      )}
    </div>
  );
};

const AdList = ({ ads, tone }) => {
  const palette = tone === 'top'
    ? { border: 'border-emerald-200 dark:border-emerald-800', headerBg: 'bg-emerald-50 dark:bg-emerald-900/20', headerBorder: 'border-emerald-200 dark:border-emerald-800', text: 'text-emerald-800 dark:text-emerald-300', ctr: 'text-emerald-600 dark:text-emerald-400', badge: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300', icon: 'text-emerald-600 dark:text-emerald-400', arrow: 'M5 15l7-7 7 7', title: 'Top Performing Ads', empty: 'No top-performing ads for this selection.' }
    : { border: 'border-amber-200 dark:border-amber-800', headerBg: 'bg-amber-50 dark:bg-amber-900/20', headerBorder: 'border-amber-200 dark:border-amber-800', text: 'text-amber-800 dark:text-amber-300', ctr: 'text-amber-600 dark:text-amber-400', badge: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300', icon: 'text-amber-600 dark:text-amber-400', arrow: 'M19 9l-7 7-7-7', title: 'Underperforming Ads', empty: 'No underperforming ads for this selection.' };

  return (
    <div className={`rounded-lg border ${palette.border} overflow-hidden`}>
      <div className={`px-4 py-3 ${palette.headerBg} border-b ${palette.headerBorder}`}>
        <div className="flex items-center gap-2">
          <svg className={`w-4 h-4 ${palette.icon}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d={palette.arrow} />
          </svg>
          <span className={`text-sm font-semibold ${palette.text}`}>{palette.title}</span>
        </div>
      </div>
      {ads.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">{palette.empty}</p>
      ) : (
        <div className="divide-y divide-gray-100 dark:divide-gray-800">
          {ads.map((ad, index) => (
            <AdCard key={`${tone}-${ad.ad_id || index}`} ad={ad} rank={index + 1} palette={palette} />
          ))}
        </div>
      )}
    </div>
  );
};

const AdCopyInsights = ({ adCopyInsights, loading, error }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-4 sm:p-5">
    <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4 inline-flex items-center">
      Ad Copy Insights
      <InfoTip text="Headlines and descriptions ranked by the clicks and conversions they generated. Each chip is one headline from the ad — useful for spotting copy that's worth doubling down on or cutting." />
    </h2>

    {error ? (
      <ErrorState message={error} />
    ) : loading ? (
      <SkeletonText lines={5} lineClass="h-14" gapClass="space-y-2" />
    ) : !adCopyInsights?.top?.length && !adCopyInsights?.bottom?.length ? (
      <p className="text-sm text-gray-500 dark:text-gray-400 py-6 text-center">
        Not enough ad data to determine top/bottom performers (minimum 50 impressions required).
      </p>
    ) : (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <AdList ads={adCopyInsights.top || []} tone="top" />
        <AdList ads={adCopyInsights.bottom || []} tone="bottom" />
      </div>
    )}
  </div>
);

export default AdCopyInsights;
